/**
 * useMarketplaceRequestList — the pending marketplace-requests LIST
 * (`GET /api/marketplace/pro/requests`), for consumers that need the rows
 * rather than just a count (the header's Home badge and Home's Needs list feed
 * them to `buildNeedsStream`).
 *
 * A thin subscription to `stores/marketplaceRequestList.ts`: every mounted
 * caller shares one in-flight request and one cached result, and
 * `bumpMarketplaceRequests` refetches it once, so a confirm/decline elsewhere
 * updates every reader without a reload and the badge and list cannot
 * disagree.
 *
 * `null` = not loaded (gate not on, still loading, or the fetch failed); the
 * needs stream treats it as "no requests". `enabled=false` issues no request.
 *
 * `useMarketplaceRequestListState` also reports `settled` (the read for the
 * current bump resolved or failed, or none is issued), which Home's settle rule
 * needs and `null` alone cannot say.
 */
import { useEffect, useRef } from "react";
import type { MarketplaceConnectionRequest } from "../lib/api";
import { useMarketplaceRequestListStore } from "../stores/marketplaceRequestList";
import { useMarketplaceRequestsRefreshStore } from "../stores/marketplaceRequests";
import { useProToken } from "./useProToken";

export interface MarketplaceRequestListState {
  rows: MarketplaceConnectionRequest[] | null;
  /** True once the read resolved or failed; also true when none is issued. */
  settled: boolean;
}

export function useMarketplaceRequestList(
  enabled: boolean,
): MarketplaceConnectionRequest[] | null {
  return useMarketplaceRequestListState(enabled).rows;
}

export function useMarketplaceRequestListState(enabled: boolean): MarketplaceRequestListState {
  const getToken = useProToken();
  // Attach once per `enabled` flip, not per token-resolver identity: a changed
  // identity must not look like a fresh mount that retries a failed read.
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);
  useEffect(() => {
    if (!enabled) return;
    return useMarketplaceRequestListStore
      .getState()
      .attach({ getToken: () => getTokenRef.current() });
  }, [enabled]);
  const rows = useMarketplaceRequestListStore((s) => s.rows);
  const loadedVersion = useMarketplaceRequestListStore((s) => s.loadedVersion);
  const refreshVersion = useMarketplaceRequestsRefreshStore((s) => s.version);

  return {
    rows: enabled ? rows : null,
    settled: !enabled || loadedVersion === refreshVersion,
  };
}
